/**
 * Provedores de contexto que envolvem toda a aplicação.
 *
 * O quê: monta a autenticação e o cliente do TanStack Query e os disponibiliza
 * à árvore de componentes. É o ponto único onde se altera o comportamento
 * padrão de cache e de repetição de requisição.
 *
 * Como: o cliente é criado uma única vez, via `useState` com função de
 * inicialização, e não no corpo do módulo. Criar no corpo do módulo funciona em
 * produção mas quebra o Fast Refresh em desenvolvimento, porque cada recarga
 * geraria um cliente novo e o cache seria descartado a cada salvamento.
 *
 * A autenticação fica por fora do Query, e não o contrário: toda consulta
 * precisa de token, então o provedor que o fornece tem de estar montado antes
 * de qualquer requisição partir.
 *
 * Utiliza: @tanstack/react-query, o ProvedorAuth e `ErroDaApi` de
 * `@/shared/api`.
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ErroDaApi } from '@/shared/api';
import { ProvedorAuth } from './provedor-auth';

interface ProvidersProps {
  children: ReactNode;
}

/**
 * Envolve a aplicação com os provedores globais.
 *
 * Os padrões de cache foram escolhidos para o perfil de uso do escritório:
 * poucas pessoas, editando dados que mudam devagar. `staleTime` de 5 minutos
 * evita refazer a mesma consulta a cada troca de tela, e desligar o refetch ao
 * focar a janela impede uma enxurrada de requisições quando alguém volta de
 * outra aba depois do almoço. Erro de autorização (401 e 403) não é repetido,
 * porque repetir não resolve — só atrasa a mensagem para o usuário.
 *
 * @param children - Árvore da aplicação a ser envolvida.
 * @returns Os filhos dentro dos provedores de contexto.
 */
export function Providers({ children }: ProvidersProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: (contagemFalhas, erro) => {
              if (erro instanceof ErroDaApi && (erro.status === 401 || erro.status === 403)) {
                return false;
              }
              return contagemFalhas < 2;
            },
          },
        },
      }),
  );

  return (
    <ProvedorAuth>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </ProvedorAuth>
  );
}
